import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, useWatch } from 'react-hook-form'
import FormButtons from 'features/contracts/components/form-buttons/FormButtons'
import { LETTERS_NUMBERS_SLASHES_DASHES } from 'constants/regex'
import { isValidPostalCode } from 'utils/addContract'
import { getTown } from 'lib/addContract'
import { Towns } from 'lib/types'
import { AddContractStepProps, AddContractSteps, AddressFormInputs, AddressFormInputsWithOptionalPostalCode } from '../../types.ts'
import { Form, Heading, Select, TextField, Toggle } from '@boltenergy-be/design-system'
import { ContractFlowTrackingTypes } from 'types/tracking.ts'
import styles from './AddressStep.module.scss'
import contractStyles from '../../../Contracts.module.scss'
import { GAS_PRODUCT_CODES, getProductFromProductCode } from '@boltenergy-be/shared'
import { AddressWithCountryAndTownCode } from 'types/general.ts'

const AddressStep = ({ setNextStep, addContractData, setAddContractData, handleEanLookup, isMove }: AddContractStepProps) => {
  // i18n
  const { t } = useTranslation(['contracts', 'validation', 'common'])

  // Local state
  const [towns, setTowns] = useState<Towns>([])

  // React Hook Form
  const hookForm = useForm<AddressFormInputsWithOptionalPostalCode>({
    mode: 'onBlur',
    defaultValues: {
      ...addContractData[AddContractSteps.ADDRESS],
      deliveryAddress: {
        ...addContractData[AddContractSteps.ADDRESS].deliveryAddress,
        postalCode:
          addContractData[AddContractSteps.ADDRESS].deliveryAddress.postalCode === 0
            ? undefined
            : addContractData[AddContractSteps.ADDRESS].deliveryAddress.postalCode
      }
    }
  })
  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields },
    setValue,
    control
  } = hookForm
  const watchTownName = useWatch({ control, name: 'deliveryAddress.townName' })
  const watchNeedsGas = useWatch({ control, name: 'needsGas' })

  /**
   * Triggered on first render
   */
  useEffect(() => {
    // Checks if town names should be fetched based on postalCode in store
    const postalCode = addContractData[AddContractSteps.ADDRESS].deliveryAddress.postalCode
    if (postalCode) {
      void validatePostalCodeAndFetchTowns(postalCode)
    }
  }, []) // oxlint-disable-line react-hooks/exhaustive-deps

  /**
   * Handles the submit after validation by React Hook Form
   *
   * @param {AddressFormInputs} data
   */
  const onSubmit = async (data: AddressFormInputsWithOptionalPostalCode) => {
    if (!data.deliveryAddress.postalCode || data.deliveryAddress.postalCode === 0) {
      return
    }

    // Perform EAN lookup
    if (handleEanLookup && 'deliveryAddress' in dirtyFields) handleEanLookup(data.deliveryAddress as AddressWithCountryAndTownCode)

    setNextStep(data as AddressFormInputs, isMove ? AddContractSteps.REFERRAL : AddContractSteps.PRODUCER)
  }

  /**
   * Fetches the town names based on the given postalCode
   *
   * @param {number} postalCode
   */
  const validatePostalCodeAndFetchTowns = async (postalCode: number) => {
    if (isValidPostalCode(postalCode)) {
      const response = await getTown(postalCode)

      if (response !== null) {
        const { towns, region } = response
        setTowns(towns)

        if (!towns.some((town) => town.townName === watchTownName)) {
          setValue('deliveryAddress.townName', towns[0].townName)
          setValue('region', region)
        }

        return true
      }
    }

    return t(($) => $.invalid.postalCode, {
      ns: 'validation'
    })
  }

  /**
   * Handles the town select change & sets the town code
   *
   * @param {string} selectedTown
   */
  const handleTownSelectChange = (selectedTown: string) => {
    const town = towns.find((town) => town.townName === selectedTown)
    const townCode = town?.townCode ?? 0
    const townName = town?.townName ?? ''

    setValue('deliveryAddress.townCode', townCode)
    setValue('deliveryAddress.townName', townName)
  }

  return (
    <section className={styles['address-section']}>
      <Heading as="h1" variant="h4">
        {t(($) => $.add.steps.address.title)}
      </Heading>
      <Form onSubmit={handleSubmit(onSubmit)} className={contractStyles['narrow-grid']}>
        {/* DELIVERY ADDRESS STREET */}
        <TextField
          id="deliveryAddress.streetName"
          label={t(($) => $.add.steps.address.form.deliveryAddress.streetName)}
          {...register('deliveryAddress.streetName', {
            required: t(($) => $.required, {
              ns: 'validation'
            })
          })}
          error={errors?.deliveryAddress?.streetName?.message}
        />

        <TextField
          id="deliveryAddress.streetNumber"
          label={t(($) => $.add.steps.address.form.deliveryAddress.streetNumber)}
          {...register('deliveryAddress.streetNumber', {
            required: t(($) => $.required, {
              ns: 'validation'
            }),
            maxLength: {
              value: 5,
              message: t(($) => $.invalid.length, {
                length: 5,
                ns: 'validation'
              })
            },
            pattern: {
              value: LETTERS_NUMBERS_SLASHES_DASHES,
              message: t(($) => $.invalid.streetNumber, {
                ns: 'validation'
              })
            }
          })}
          maxLength={5}
          error={errors?.deliveryAddress?.streetNumber?.message}
          grid="span 1"
        />

        <TextField
          id="deliveryAddress.streetBox"
          label={t(($) => $.add.steps.address.form.deliveryAddress.streetBox)}
          {...register('deliveryAddress.streetBox', {
            maxLength: {
              value: 5,
              message: t(($) => $.invalid.length, {
                length: 5,
                ns: 'validation'
              })
            },
            pattern: {
              value: LETTERS_NUMBERS_SLASHES_DASHES,
              message: t(($) => $.invalid.streetBox, {
                ns: 'validation'
              })
            }
          })}
          maxLength={5}
          error={errors?.deliveryAddress?.streetBox?.message}
          grid="span 1"
        />

        {/* DELIVERY ADDRESS POSTAL CODE */}
        <TextField
          id="deliveryAddress.postalCode"
          label={t(($) => $.add.steps.address.form.deliveryAddress.postalCode)}
          {...register('deliveryAddress.postalCode', {
            required: t(($) => $.required, {
              ns: 'validation'
            }),
            validate: (val) => (val ? validatePostalCodeAndFetchTowns(val) : true)
          })}
          type="number"
          min={1000}
          max={9992}
          error={errors?.deliveryAddress?.postalCode?.message}
          inputMode="numeric"
          onChange={(e) => {
            const value = e.target.valueAsNumber
            setValue('deliveryAddress.postalCode', value)
            void validatePostalCodeAndFetchTowns(value)
          }}
        />

        {/* DELIVERY ADDRESS TOWN NAME */}
        <Select
          id="address.townName"
          label={t(($) => $.add.steps.address.form.deliveryAddress.townName)}
          {...register('deliveryAddress.townName', {
            required: t(($) => $.required, {
              ns: 'validation'
            })
          })}
          value={watchTownName}
          error={errors?.deliveryAddress?.townName?.message}
          onChange={(e) => handleTownSelectChange(e.target.value)}
        >
          {towns.map((town) => (
            <option key={town.townCode} value={town.townName}>
              {town.townName}
            </option>
          ))}
        </Select>

        <div className={styles['toggle-group']}>
          <strong>{t(($) => $.add.steps.address.form.fuelType.title)}</strong>
          <Toggle
            isFullwidth
            active={watchNeedsGas}
            options={[
              { label: t(($) => $.add.steps.address.form.fuelType.labels.onlyElec), value: false },
              { label: t(($) => $.add.steps.address.form.fuelType.labels.elecAndGas), value: true }
            ]}
            onClick={(needsGas) => {
              setValue('needsGas', needsGas)
              const product = getProductFromProductCode(addContractData[AddContractSteps.PRODUCT].electricity)
              setAddContractData({
                ...addContractData,
                [AddContractSteps.PRODUCT]: {
                  ...addContractData[AddContractSteps.PRODUCT],
                  gas: needsGas && product ? GAS_PRODUCT_CODES[product] : undefined
                }
              })
            }}
          />
        </div>

        <FormButtons currentStep={AddContractSteps.ADDRESS} trackingId={ContractFlowTrackingTypes.ADD} />
      </Form>
    </section>
  )
}

export default AddressStep
